import type { WizardData, WizardField, WizardFieldError, WizardOption, WizardSchema, WizardStep } from '@hive/api';
import type { CommonKey, Params } from '@hive/i18n';

/** RF-NOC-01: nenhum assistente passa de 7 passos. */
export const MAX_STEPS = 7;

type T = (k: CommonKey, p?: Params) => string;
type L = (v: unknown) => string;

/** Opções de um campo, aceitando `{value,label}` (assistentes) ou texto simples (catálogo de integrações). */
export function fieldOptions(f: WizardField, l: L): Array<{ value: string; label: string }> {
  return (f.options ?? []).map((o: WizardOption | string) =>
    typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: l(o.label) || o.value },
  );
}

export function isHidden(f: WizardField, data: WizardData): boolean {
  if (f.showIf) {
    for (const [k, want] of Object.entries(f.showIf)) {
      const v = String(data[k] ?? '');
      if (!(Array.isArray(want) ? want : [want]).includes(v)) return true;
    }
  }
  if (!f.hideWhen) return false;
  const other = data[f.hideWhen.field];
  return other != null && String(other) === String(f.hideWhen.equals);
}

export function visibleFields(step: WizardStep, data: WizardData): WizardField[] {
  return (step.fields ?? []).filter((f) => !isHidden(f, data));
}

/** Valores iniciais: o padrão de cada campo, sem sobrescrever o que já veio preenchido. */
export function initialData(schema: WizardSchema, given?: WizardData): WizardData {
  const d: WizardData = {};
  for (const s of schema.steps) {
    for (const f of s.fields ?? []) {
      if (f.default !== undefined && f.type !== 'secret') d[f.id] = f.default;
    }
  }
  return { ...d, ...(given ?? {}) };
}

function blank(v: unknown): boolean {
  return v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);
}

/**
 * R12-01 — retoma um rascunho sem apagar o padrão da tela: campo vazio no rascunho fica com o valor que a tela
 * já tinha (endereço detectado pelo servidor, validade padrão); só o que a pessoa preencheu de fato volta.
 */
export function mergeDraft(base: WizardData, saved: WizardData): WizardData {
  const out: WizardData = { ...base };
  for (const [k, v] of Object.entries(saved)) {
    if (blank(v) && !blank(base[k])) continue;
    out[k] = v;
  }
  return out;
}

/** Valor detectado pelo servidor (`detected: true` com padrão) diferente do atual (trocado ou apagado): a tela oferece "usar o detectado". */
export function detectedValue(f: WizardField, current: unknown): string | undefined {
  if (!f.detected || typeof f.default !== 'string' || !f.default.trim()) return undefined;
  const cur = current == null ? '' : String(current).trim();
  return cur !== f.default.trim() ? f.default : undefined;
}

export function isSecret(f: WizardField): boolean {
  return f.type === 'secret';
}

/** Tira os segredos (rascunhos nunca guardam segredo). */
export function withoutSecrets(schema: WizardSchema, data: WizardData): WizardData {
  const out: WizardData = { ...data };
  for (const s of schema.steps) for (const f of s.fields ?? []) if (isSecret(f)) delete out[f.id];
  return out;
}

/**
 * O que vai para o rascunho: sem segredos e sem o valor detectado que a pessoa não trocou (R12-01) — ao reabrir,
 * vale o detectado do momento (o servidor pode ter mudado de endereço); o valor digitado fica.
 */
export function forDraft(schema: WizardSchema, data: WizardData): WizardData {
  const out = withoutSecrets(schema, data);
  for (const s of schema.steps)
    for (const f of s.fields ?? []) {
      const v = out[f.id];
      if (f.detected && typeof f.default === 'string' && (v == null || String(v).trim() === f.default.trim())) delete out[f.id];
    }
  return out;
}

export function lines(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (v == null) return [];
  return String(v)
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isEmpty(v: unknown): boolean {
  return v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CIDR = /^[0-9a-fA-F:.]+(\/\d{1,3})?$/;

/**
 * Validação local do passo, com as mesmas regras do validador do núcleo (WizardValidator).
 * Serve de primeira barreira (resposta imediata) e para assistentes sem rota de validação.
 */
export function validateLocal(step: WizardStep, data: WizardData, t: T, l: L): Record<string, WizardFieldError> {
  const errors: Record<string, WizardFieldError> = {};
  for (const f of visibleFields(step, data)) {
    const v = data[f.id];
    if (isEmpty(v)) {
      if (f.required) errors[f.id] = { what: t('wz.v.required'), fix: t('wz.v.requiredFix') };
      continue;
    }
    const s = typeof v === 'string' ? v.trim() : '';
    switch (f.type) {
      case 'email':
        if (!EMAIL.test(s)) errors[f.id] = { what: t('wz.v.email', { value: s }), fix: t('wz.v.emailFix') };
        break;
      case 'email-list': {
        const items = lines(v);
        const bad = items.find((e) => !EMAIL.test(e));
        if (bad) errors[f.id] = { what: t('wz.v.email', { value: bad }), fix: t('wz.v.emailFix') };
        else if (f.maxItems != null && items.length > f.maxItems) errors[f.id] = { what: t('wz.v.tooMany', { n: f.maxItems }) };
        break;
      }
      case 'cidr-list': {
        const bad = lines(v).find((c) => !CIDR.test(c));
        if (bad) errors[f.id] = { what: t('wz.v.cidr', { value: bad }), fix: t('wz.v.cidrFix') };
        break;
      }
      case 'url':
        if (!/^https?:\/\/\S+$/.test(s)) errors[f.id] = { what: t('wz.v.url'), fix: t('wz.v.urlFix') };
        break;
      case 'number':
        if (Number.isNaN(Number(v))) errors[f.id] = { what: t('wz.v.number') };
        else if ((f.min != null && Number(v) < f.min) || (f.max != null && Number(v) > f.max)) errors[f.id] = { what: t('wz.v.range') };
        break;
      default:
        break;
    }
    if (errors[f.id]) continue;
    if (f.minItems != null && Array.isArray(v) && v.length < f.minItems) errors[f.id] = { what: t('wz.v.minItems', { n: f.minItems }) };
    else if (f.maxLength != null && typeof v === 'string' && v.length > f.maxLength) errors[f.id] = { what: t('wz.v.maxLength', { n: f.maxLength }) };
    else if (f.pattern && typeof v === 'string' && !new RegExp(f.pattern).test(v)) {
      errors[f.id] = {
        what: f.patternMessage ? l(f.patternMessage) : t('wz.v.pattern'),
        fix: f.example ? t('wz.example', { value: f.example }) : undefined,
      };
    }
  }
  return errors;
}

/** Confere o limite de passos (HU-015: assistente acima de 7 passos não pode ser publicado). */
export function assertStepLimit(schema: WizardSchema): void {
  if (schema.steps.length > MAX_STEPS) {
    throw new Error(`Assistente "${schema.id}" tem ${schema.steps.length} passos; o limite é ${MAX_STEPS} (RF-NOC-01).`);
  }
}
